import { Card } from '@mui/material';
import { styled } from '@mui/system';

export const ButtonContainer = styled(Card)<any>(() => ({
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  background: '#FFFFFF',
  position: 'relative',
  padding: '1rem 1.15rem 1.15rem',
  gap: '.6rem',
  borderRadius: '.65rem',
  transition: 'box-shadow .15s ease-out, transform .1s ease-out',

  '&:hover': {
    boxShadow: '0 4px 16px rgba(22,53,103,.14)',
    transform: 'translateY(-1px)',
  },

  // Show Work: amber left-edge stripe when a student has pasted more than
  // HEAVY_PASTE_WORD_THRESHOLD words. Subtle — surfaces at a glance without
  // shouting. Same treatment a future "needs review" state can reuse.
  '&.heavy-paste': {
    borderLeft: '3px solid #E88F25',
    paddingLeft: 'calc(1.15rem - 3px)',
  },

  '.card-header': {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    minWidth: 0,

    '.avatar': {
      width: '1.25rem',
      height: '1.25rem',
      borderRadius: '50%',
      flexShrink: 0,
    },

    '.student-name': {
      color: '#163567',
      fontWeight: 700,
      fontSize: '.9rem',
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
      flex: 1,
    },

    '.expand-toggle': {
      flexShrink: 0,
      width: '1.25rem',
      height: '1.25rem',
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'transparent',
      color: '#999',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      transition: 'transform .15s ease-out, background-color .15s ease-out, color .15s ease-out',

      '&:hover': {
        backgroundColor: '#F0F0F5',
        color: '#163567',
      },

      '&.open': {
        transform: 'rotate(180deg)',
        color: '#163567',
      },
    },
  },

  '.status-row': {
    display: 'flex',
    gap: '.25rem',

    '& > *': {
      whiteSpace: 'nowrap',
    },
  },

  '.progress-block': {
    display: 'flex',
    flexDirection: 'column',
    gap: '.4rem',
    transition: 'opacity .15s ease-out',

    '&.not-started': {
      opacity: 0.6,
    },

    '.progress-line': {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: '.5rem',
      fontFamily: 'Inter, sans-serif',

      '.word-count-pill': {
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '.3rem',

        strong: {
          fontSize: '1.35rem',
          fontWeight: 800,
          color: '#163567',
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        },

        '.word-count-label': {
          color: '#999',
          fontSize: '.72rem',
          fontWeight: 500,
        },
      },

      '.blocks-pill': {
        fontSize: '.72rem',
        color: '#666',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',

        '.metric-sep': {
          color: '#CCC',
          margin: '0 1px',
        },
      },
    },

    '.progress-bar': {
      height: '.45rem',
      backgroundColor: '#EAEAF0',
      borderRadius: '999px',
      overflow: 'hidden',

      '.progress-bar-fill': {
        height: '100%',
        backgroundColor: '#5C4FB8',
        borderRadius: '999px',
        transition: 'width .25s ease-out',
      },
    },
  },

  '.metric-details': {
    display: 'flex',
    flexDirection: 'column',
    gap: '.35rem',
    paddingTop: '.55rem',
    borderTop: '1px solid rgba(22,53,103,.08)',
    transition: 'opacity .15s ease-out',

    '&.not-started': {
      opacity: 0.6,
    },

    '.metric-row': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '.5rem',
      fontFamily: 'Inter, sans-serif',
      fontSize: '.78rem',
      lineHeight: 1.3,

      '.metric-name': {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '.3rem',
        color: '#666',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,

        '.metric-icon': {
          fontSize: '.9rem',
          lineHeight: 1,
          flexShrink: 0,
        },
      },

      '.metric-value': {
        fontVariantNumeric: 'tabular-nums',
        color: '#999',
        whiteSpace: 'nowrap',
        flexShrink: 0,

        strong: {
          color: '#163567',
          fontSize: '.86rem',
          fontWeight: 700,
          marginRight: '1px',
        },

        '.metric-sep': {
          color: '#CCC',
          margin: '0 2px',
        },
      },

      '&.heart .metric-name': { color: '#D14F73' },
      '&.heart .metric-name .metric-icon': { color: '#D14F73' },
      '&.heart .metric-value strong': { color: '#D14F73' },

      '&.important .metric-name': { color: '#E88F25' },
      '&.important .metric-name .metric-icon': { color: '#E88F25' },
      '&.important .metric-value strong': { color: '#E88F25' },

      '&.signal .metric-name': { color: '#2E2C75' },
      '&.signal .metric-name .metric-icon': { color: '#2E2C75' },
      '&.signal .metric-value strong': { color: '#2E2C75' },

      // Show Work — pacing row stays neutral (informational, not a flag).
      '&.pace .metric-name': { color: '#666' },
      '&.pace .metric-value strong': { color: '#163567' },

      // Show Work — paste row is gray by default; amber when above threshold.
      '&.paste .metric-name': { color: '#666' },
      '&.paste .metric-value strong': { color: '#163567' },
      '&.paste .paste-unit': { color: '#999', fontWeight: 400, marginLeft: '.1rem' },
      '&.paste.heavy .metric-name': { color: '#B5651D' },
      '&.paste.heavy .metric-value strong': { color: '#B5651D' },
    },
  },

  '.not-started-hint': {
    position: 'absolute',
    bottom: '.5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#2C2C2C',
    color: '#FFFFFF',
    fontFamily: 'Inter, sans-serif',
    fontSize: '.72rem',
    fontWeight: 600,
    padding: '.3rem .65rem',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,.18)',
    animation: 'not-started-pop .2s ease-out',
  },

  '@keyframes not-started-pop': {
    from: { opacity: 0, transform: 'translateX(-50%) translateY(4px)' },
    to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
  },
}));
